'use client'
import { FC, useState } from 'react'
import { Button, Input } from '@/root/ui/tuuleliinid'
import { useGetTranslations, useCustomRouter } from '@/root/shared-hooks'
import { useForm, FieldValues } from 'react-hook-form'
import { isValidEmailOrPhoneNumber } from '@/root/shared-helpers'
import { passwordResetPrepare } from '@/root/shared-api/auth'

interface RecoveryEmailFormProps {
  onSubmit: () => void
}

export const RecoveryEmailForm: FC<RecoveryEmailFormProps> = ({ onSubmit }) => {
  const { getTranslation } = useGetTranslations()
  const { locale, push } = useCustomRouter()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      login: '',
    },
    mode: 'onBlur',
  })

  const loginValue = watch('login')
  const isDisabled = [isLoading, errors.login, !loginValue].some(Boolean)

  const onSubmitForm = async (data: FieldValues) => {
    if (isLoading) return
    setIsLoading(true)
    try {
      await passwordResetPrepare(data.login)
    } catch {
      // Silently ignore — do not reveal whether the email exists
    } finally {
      setIsLoading(false)
      onSubmit()
    }
  }

  const handleCancel = () => {
    push(`/${locale}/auth/login`)
  }

  return (
    <form className="flex w-full min-w-90 max-w-110 flex-col" onSubmit={handleSubmit(onSubmitForm)}>
      <Input
        type="text"
        label={getTranslation('recoveryEmailLabel')}
        placeholder={getTranslation('authFieldPlaceholder')}
        error={errors.login?.message as string}
        {...register('login', {
          required: getTranslation('requiredError'),
          validate: (value) => isValidEmailOrPhoneNumber(value) || getTranslation('invalidFormatText'),
        })}
      />

      <div className="mt-6 flex gap-4">
        <Button
          type="button"
          onClick={handleCancel}
          variant="default"
          className="border-neutral-medium-light text-neutral-darkest flex h-12 w-1/2 items-center justify-center rounded-lg border text-lg font-medium transition-colors hover:border-primary-base hover:text-primary-base"
        >
          {getTranslation('buttonCancel')}
        </Button>
        <Button
          className="flex h-12 w-1/2 items-center justify-center whitespace-nowrap rounded-lg text-lg font-bold"
          disabled={isDisabled}
          variant="gray"
          type="submit"
        >
          {isLoading ? getTranslation('authSending') : getTranslation('recoverySendButton')}
        </Button>
      </div>
    </form>
  )
}
